// The live call: everyone's video, mic/camera toggles, leave, and (for the
// host) the guest link, removing a guest, and record/stop. Everyone records
// locally and uploads as they go (see recording-controller, upload-queue).

import { useEffect, useRef, useState } from 'react';
import { SignalingClient, studioWsUrl, type ServerMessage } from '../../lib/studio/signaling-client';
import { ClockSync } from '../../lib/studio/clock-sync';
import { PeerMesh } from '../../lib/studio/peer-mesh';
import { fetchIceServers, type StudioSession } from '../../lib/studio/studio-api';
import { stopStream } from '../../lib/studio/media';
import { openChunkStore, type ChunkStore } from '../../lib/studio/chunk-store';
import { UploadQueue, type UploadStats } from '../../lib/studio/upload-queue';
import { RecordingController, type RecView } from '../../lib/studio/recording-controller';
import { Button } from '../ui/Button';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { CopyField } from '../shows/bits';
import { VideoTile } from './VideoTile';
import { HostUploads, RecBanner, UploadStatus } from './Recording';

interface Person {
  id: string;
  displayName: string;
  role: string;
  media: { mic: boolean; cam: boolean };
}

export interface StudioCallProps {
  session: StudioSession;
  displayName: string;
  stream: MediaStream;
  isHost: boolean;
  inviteUrl?: string | null;
  relayOnly?: boolean;
  /** Hold uploads this many seconds (testing resilience). */
  simulateOfflineS?: number;
  onLeave: (reason: 'left' | 'kicked') => void;
  /** Injection points for tests. */
  deps?: {
    makeClient?: (token: string) => SignalingClient;
    fetchIce?: (token: string) => Promise<RTCIceServer[]>;
    PeerConnection?: typeof RTCPeerConnection;
    openStore?: () => Promise<ChunkStore>;
    Recorder?: typeof MediaRecorder;
    isTypeSupported?: (mime: string) => boolean;
  };
}

const IDLE: RecView = { phase: 'idle', take: null, lastTakeId: null, error: null };
const NO_UPLOADS: UploadStats = { state: 'idle', pending: 0, sent: 0, dropped: 0, missing: 0 };

export function StudioCall({ session, displayName, stream, isHost, inviteUrl, relayOnly, simulateOfflineS = 0, onLeave, deps }: StudioCallProps) {
  const [people, setPeople] = useState<Map<string, Person>>(new Map());
  const [remote, setRemote] = useState<Map<string, MediaStream | null>>(new Map());
  const [peerState, setPeerState] = useState<Map<string, RTCPeerConnectionState>>(new Map());
  const [connection, setConnection] = useState<string>('connecting');
  const [mic, setMic] = useState(true);
  const [cam, setCam] = useState(() => stream.getVideoTracks().length > 0);
  const [kicking, setKicking] = useState<Person | null>(null);
  const [rec, setRec] = useState<RecView>(IDLE);
  const [uploads, setUploads] = useState<UploadStats>(NO_UPLOADS);
  const [confirmLeave, setConfirmLeave] = useState(false);
  const client = useRef<SignalingClient | null>(null);
  const recorder = useRef<RecordingController | null>(null);
  const leaveRef = useRef(onLeave);
  leaveRef.current = onLeave;

  useEffect(() => {
    let cancelled = false;
    let mesh: PeerMesh | null = null;
    let queue: UploadQueue | null = null;
    let controller: RecordingController | null = null;
    const online = () => queue?.retryNow();
    window.addEventListener('online', online);
    const c = deps?.makeClient?.(session.token) ?? new SignalingClient({ url: studioWsUrl(), token: session.token });
    client.current = c;
    const sync = new ClockSync(c);

    const upsert = (p: Person) => setPeople((m) => new Map(m).set(p.id, p));
    const offs = [
      c.on('state', (m) => setConnection(String(m.state))),
      c.on('welcome', (m) => {
        const others = (m.participants as Person[] | undefined) ?? [];
        setPeople(new Map(others.map((p) => [p.id, p])));
        if (!sync.synced) sync.start();
      }),
      c.on('peer.joined', (m) => upsert(m.participant as Person)),
      c.on('peer.left', (m: ServerMessage) =>
        setPeople((map) => {
          const next = new Map(map);
          next.delete(String(m.participantId));
          return next;
        }),
      ),
      c.on('peer.state', (m) =>
        setPeople((map) => {
          const p = map.get(String(m.participantId));
          return p ? new Map(map).set(p.id, { ...p, media: m.media as Person['media'] }) : map;
        }),
      ),
      c.on('kicked', () => leaveRef.current('kicked')),
    ];

    (async () => {
      const iceServers = await (deps?.fetchIce ?? fetchIceServers)(session.token).catch(() => [] as RTCIceServer[]);
      const store = await (deps?.openStore ?? openChunkStore)();
      if (cancelled) return;
      queue = new UploadQueue({
        token: session.token,
        participantId: session.participantId,
        store,
        pausedUntil: simulateOfflineS ? Date.now() + simulateOfflineS * 1000 : 0,
      });
      queue.onChange(setUploads);
      // Anything left from an earlier visit (a reload mid-take) goes up first.
      void queue.resume();
      controller = new RecordingController({
        channel: c,
        clock: sync,
        stream,
        participantId: session.participantId,
        queue,
        onChange: setRec,
        Recorder: deps?.Recorder,
        isTypeSupported: deps?.isTypeSupported,
      });
      controller.start();
      recorder.current = controller;
      mesh = new PeerMesh({
        selfId: session.participantId,
        channel: c,
        iceServers,
        localStream: stream,
        relayOnly,
        PeerConnection: deps?.PeerConnection,
        onRemoteStream: (id, s) => setRemote((m) => new Map(m).set(id, s)),
        onPeerState: (id, s) => setPeerState((m) => new Map(m).set(id, s)),
      });
      // Subscribe before connecting, so the welcome's peer list isn't missed.
      mesh.start();
      c.connect();
    })();

    return () => {
      cancelled = true;
      window.removeEventListener('online', online);
      offs.forEach((off) => off());
      // Stop recording and save the last chunks; the rest uploads next visit.
      const q = queue;
      void controller?.close().then(() => q?.close());
      recorder.current = null;
      sync.stop();
      mesh?.close();
      c.close();
    };
    // The call lives for one session and stream.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.token, session.participantId, stream, relayOnly, simulateOfflineS]);

  const toggle = (kind: 'mic' | 'cam') => {
    const nextMic = kind === 'mic' ? !mic : mic;
    const nextCam = kind === 'cam' ? !cam : cam;
    stream.getAudioTracks().forEach((t) => (t.enabled = nextMic));
    stream.getVideoTracks().forEach((t) => (t.enabled = nextCam));
    setMic(nextMic);
    setCam(nextCam);
    client.current?.send({ type: 'media.state', mic: nextMic, cam: nextCam });
  };

  const busy = rec.phase !== 'idle' || uploads.pending > 0;
  useEffect(() => {
    if (!busy) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [busy]);

  const leave = () => {
    stopStream(stream);
    onLeave('left');
  };

  const others = [...people.values()];

  return (
    <div className="space-y-4">
      <RecBanner
        view={rec}
        countdownMs={() => recorder.current?.countdownMs() ?? 0}
        elapsedMs={() => recorder.current?.elapsedMs() ?? 0}
      />
      {connection !== 'open' && (
        <p role="status" className="rounded-md bg-warning-soft px-3 py-2 text-sm text-warning">
          {connection === 'connecting' ? 'Connecting to the studio…' : 'Connection lost — reconnecting…'}
        </p>
      )}
      <div className={`grid gap-3 ${others.length === 0 ? 'max-w-2xl' : others.length < 3 ? 'md:grid-cols-2' : 'md:grid-cols-3'}`}>
        <VideoTile stream={stream} name={displayName} self mic={mic} cam={cam} />
        {others.map((p) => (
          <VideoTile
            key={p.id}
            stream={remote.get(p.id) ?? null}
            name={p.displayName}
            mic={p.media.mic}
            cam={p.media.cam}
            state={peerState.get(p.id) ?? 'new'}
            actions={
              isHost && p.role === 'guest' ? (
                <button
                  type="button"
                  onClick={() => setKicking(p)}
                  className="rounded bg-black/60 px-2 py-1 text-xs hover:bg-danger"
                  aria-label={`Remove ${p.displayName}`}
                >
                  Remove
                </button>
              ) : undefined
            }
          />
        ))}
      </div>
      {others.length === 0 && <p className="text-sm text-muted">Waiting for others to join…</p>}

      <div role="toolbar" aria-label="Call controls" className="flex flex-wrap items-center gap-2">
        <Button variant={mic ? 'secondary' : 'danger'} onClick={() => toggle('mic')} aria-pressed={!mic}>
          {mic ? 'Mute' : 'Unmute'}
        </Button>
        {stream.getVideoTracks().length > 0 && (
          <Button variant={cam ? 'secondary' : 'danger'} onClick={() => toggle('cam')} aria-pressed={!cam}>
            {cam ? 'Stop video' : 'Start video'}
          </Button>
        )}
        {isHost &&
          (rec.phase === 'idle' ? (
            <Button variant="danger" onClick={() => recorder.current?.requestStart()} disabled={connection !== 'open'}>
              ● Record
            </Button>
          ) : (
            <Button variant="secondary" onClick={() => recorder.current?.requestStop()}>
              ■ Stop
            </Button>
          ))}
        <Button variant="ghost" onClick={() => (busy ? setConfirmLeave(true) : leave())} className="ml-auto">
          Leave
        </Button>
      </div>
      <UploadStatus stats={uploads} recording={rec.phase === 'recording'} />
      {isHost && rec.lastTakeId && <HostUploads sessionId={rec.lastTakeId} token={session.token} />}

      {isHost && inviteUrl && (
        <div className="max-w-xl">
          <CopyField label="Guest link — anyone with it can join" value={inviteUrl} />
        </div>
      )}

      <ConfirmDialog
        open={confirmLeave}
        danger
        title={rec.phase === 'idle' ? 'Leave before your upload finishes?' : 'Leave while recording?'}
        body="What’s recorded so far is saved on this computer and finishes uploading the next time you open this studio link."
        confirmLabel="Leave"
        onCancel={() => setConfirmLeave(false)}
        onConfirm={() => {
          setConfirmLeave(false);
          leave();
        }}
      />
      <ConfirmDialog
        open={!!kicking}
        danger
        title={`Remove ${kicking?.displayName ?? 'guest'}?`}
        body="They leave the call and their link stops working for them."
        confirmLabel="Remove"
        onCancel={() => setKicking(null)}
        onConfirm={() => {
          if (kicking) client.current?.send({ type: 'kick', participantId: kicking.id });
          setKicking(null);
        }}
      />
    </div>
  );
}
